import { Text } from '@mantine/core';
import { ErrorBoundary } from '@sentry/react';
import { useIsMutating } from '@tanstack/react-query';
import { useDebtor } from 'app/Private/Clients/Detail/client-atom';
import { useCreateDebtorAiSummary } from 'app/Private/Clients/Detail/InfoTab/components/AiSummaryPanel/hooks';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { useEffect } from 'react';
import { isNullish } from 'remeda';
import { AiSummaryContent } from './AiSummaryContent';
import { AiSummaryGenerationInProgress } from './AiSummaryGenerationInProgressPanel';
import { AiSummaryLoadingPanel } from './AiSummaryLoadingPanel';
import { AiSummaryUpsellPanel } from './AiSummaryUpsellPanel';
import { useLoadDebtorAiSummary } from './hooks/use-load-debtor-ai-summary';

export const AiSummaryPanelInner = () => {
  const company = useCompanyConfiguration();
  const debtor = useDebtor();

  const { aiSummary, isAiSummaryInitialLoading } = useLoadDebtorAiSummary(
    { id: String(debtor.id) },
    { enabled: company.package.ai_enabled },
  );

  const { createDebtorAiSummary } = useCreateDebtorAiSummary();

  useEffect(() => {
    if (!company.package.ai_enabled) return;
    if (isAiSummaryInitialLoading) return;
    if (isNullish(aiSummary) || aiSummary.isOutdated)
      createDebtorAiSummary({ debtorId: String(debtor.id) });
  }, [
    isAiSummaryInitialLoading,
    aiSummary,
    createDebtorAiSummary,
    debtor.id,
    company.package.ai_enabled,
  ]);

  const isAiSummaryGenerating = useIsMutating({ mutationKey: ['createDebtorAiSummary'] }) >= 1;

  return (
    <>
      {(() => {
        if (!company.package.ai_enabled) return <AiSummaryUpsellPanel />;

        if (!company.package.can_use_ai_context_provider) return <AiSummaryUpsellPanel />;

        if (isAiSummaryInitialLoading || isNullish(aiSummary)) return <AiSummaryLoadingPanel />;

        if (isAiSummaryGenerating)
          return <AiSummaryGenerationInProgress debtorName={debtor.full_name} />;

        return <AiSummaryContent debtor={debtor} aiSummary={aiSummary} />;
      })()}
    </>
  );
};

export const AiSummaryPanel = () => (
  <ErrorBoundary fallback={<Text>Error while loading AI Summary</Text>}>
    <AiSummaryPanelInner />
  </ErrorBoundary>
);
